import dayjs from "dayjs";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  FormController,
  MuiDateFieldFormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
} from "../../../../../../../../../../../../components";
import { MAX_LENGTH_75 } from "../../../../../../../../../../../../helpers/constants";
import { getFieldRuleByCountryForLandingInformation } from "../../../../../../../../../../../../helpers/constants/countryFieldRulesUtils";
import { isNotEmpty } from "../../../../../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../../../hooks/useSiteCore";
import {
  OccupationalFunctionOtherKey,
  ProgramClosureFormKey,
} from "../../../../../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../../../../siteCore/SiteCoreKeys";

interface EmployeeInformationProps {
  pageId: string;
  userCountryDateFormat: string;
  deliveryCountry?: string;
}

export const EmployeeInformation: FC<EmployeeInformationProps> = ({
  pageId,
  userCountryDateFormat,
  deliveryCountry = "",
}) => {
  const { control, getValues, watch, resetField } = useFormContext();
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    fetchDropdownValuesByPrefix,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();
  const gridSize = { md: 4, sm: 6, xs: 12 };

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.changeStatusModelPopupKey, key);
  };

  const isFunctionalChange = useMemo(() => {
    resetField(ProgramClosureFormKey.newOccupationalFunction);
    resetField(ProgramClosureFormKey.newOccupationalFunctionOther);
    const { functionalChange } = getValues();
    return functionalChange === programSiteCoreKeys.functionalChangeYes;
  }, [watch(ProgramClosureFormKey.functionalChange)]);

  const isNewOccupationalFunctionOther = useMemo(() => {
    resetField(ProgramClosureFormKey.newOccupationalFunctionOther);
    return (
      getValues(ProgramClosureFormKey.newOccupationalFunction) ===
      OccupationalFunctionOtherKey
    );
  }, [watch(ProgramClosureFormKey.newOccupationalFunction)]);

  const newEmployeeNameRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.newEmployeeName,
  );

  const newTitleRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.newTitle,
  );

  const functionalChangeRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.functionalChange,
  );

  const industryChangeRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.industryChange,
  );

  const newEmployerVATNumberRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.newEmployerVATNumber,
  );

  const newLevelRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.newLevel,
  );

  const dateOfEndOfTrialPeriodRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.dateOfEndOfTrialPeriod,
  );

  const currentSalaryRangeRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.currentSalaryRange,
  );

  const newSalaryRangeRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.newSalaryRange,
  );

  const newEmploymentContractTypeRule =
    getFieldRuleByCountryForLandingInformation(
      deliveryCountry,
      ProgramClosureFormKey.newEmploymentContractType,
    );

  const solutionValidationDateRule = getFieldRuleByCountryForLandingInformation(
    deliveryCountry,
    ProgramClosureFormKey.solutionValidationDate,
  );

  const countrySpecificOptions = useMemo(() => {
     const newOccupationalFunctionOptions = fetchDropdownValuesByPrefix(
      programSiteCoreKeys.changeStatusModelPopupKey,
      programSiteCoreKeys.newOccupationalFunctionListPrefix,
      deliveryCountry,
    );

    const currentSalaryRangeOptions = fetchDropdownValues(
      programSiteCoreKeys.changeStatusModelPopupKey,
      programSiteCoreKeys.currentSalaryRangeListPrefix,
    );

    const newSalaryRangeOptions = fetchDropdownValues(
      programSiteCoreKeys.changeStatusModelPopupKey,
      programSiteCoreKeys.newSalaryRangeListPrefix,
    );

    const newLevelOptions = fetchDropdownValuesByPrefix(
      programSiteCoreKeys.changeStatusModelPopupKey,
      programSiteCoreKeys.newLevelListPrefix,
      deliveryCountry,
    );

    const newEmploymentContractTypeOptions = fetchDropdownValuesByPrefix(
      programSiteCoreKeys.changeStatusModelPopupKey,
      programSiteCoreKeys.newEmploymentContractTypeListPrefix,
      deliveryCountry,
    );

    return {
      newOccupationalFunctionOptions,
      currentSalaryRangeOptions,
      newSalaryRangeOptions,
      newLevelOptions,
      newEmploymentContractTypeOptions,
    };
  }, [deliveryCountry]);

  const isNotEmptyNewJobStartDate = useMemo(() => {
    const { newJobStartDate } = getValues();
    return isNotEmpty(newJobStartDate);
  }, [deliveryCountry, watch(ProgramClosureFormKey.newJobStartDate)]);

  return (
    <MuiGrid
      container
      className="form-container"
      data-testid="employee-information"
    >
      <MuiGrid size={gridSize} className="form-field">
        <FormController
          control={control}
          name={ProgramClosureFormKey.newEmployeeName}
        >
          <MuiTextField
            id={`${pageId}-${ProgramClosureFormKey.newEmployeeName}`}
            label={getLabel(programSiteCoreKeys.newEmployerName)}
            placeholder={commonLabels.inputPlaceHolder}
            maxLength={MAX_LENGTH_75}
            showCloseIcon
            requiredARIALabel={commonARIALabel.required}
            isMandatory={newEmployeeNameRule.mandatory}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className="form-field">
        <FormController control={control} name={ProgramClosureFormKey.newTitle}>
          <MuiTextField
            id={`${pageId}-${ProgramClosureFormKey.newTitle}`}
            label={getLabel(programSiteCoreKeys.newTitle)}
            placeholder={commonLabels.inputPlaceHolder}
            maxLength={MAX_LENGTH_75}
            showCloseIcon
            requiredARIALabel={commonARIALabel.required}
            isMandatory={newTitleRule.mandatory}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className="form-field">
        <FormController
          control={control}
          name={ProgramClosureFormKey.functionalChange}
        >
          <MuiSelect
            id={`${pageId}-${ProgramClosureFormKey.functionalChange}`}
            label={getLabel(programSiteCoreKeys.functionalChange)}
            placeholder={commonLabels.selectPlaceHolder}
            options={fetchDropdownValues(
              programSiteCoreKeys.changeStatusModelPopupKey,
              programSiteCoreKeys.functionalChangeList,
            )}
            visibleDefaultSelectOption
            requiredARIALabel={commonARIALabel.required}
            isMandatory={functionalChangeRule.mandatory}
          />
        </FormController>
      </MuiGrid>
      {/* <MuiGrid size={gridSize} className="form-field">
        <FormController
          control={control}
          name={ProgramClosureFormKey.contractType}
        >
          <MuiSelect
            id={`${pageId}-${ProgramClosureFormKey.contractType}`}
            label={getLabel(programSiteCoreKeys.contractType)}
            placeholder={commonLabels.selectPlaceHolder}
            options={[]}
            visibleDefaultSelectOption
          />
        </FormController>
      </MuiGrid> */}
      {isFunctionalChange && (
        <>
          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={ProgramClosureFormKey.newOccupationalFunction}
            >
              <MuiSelect
                id={`${pageId}-${ProgramClosureFormKey.newOccupationalFunction}`}
                label={getLabel(programSiteCoreKeys.newOccupationalFunction)}
                placeholder={commonLabels.selectPlaceHolder}
               options={countrySpecificOptions.newOccupationalFunctionOptions}
                visibleDefaultSelectOption
                requiredARIALabel={commonARIALabel.required}
                isMandatory={functionalChangeRule.mandatory}
              />
            </FormController>
          </MuiGrid>

          {isNewOccupationalFunctionOther && (
            <MuiGrid size={gridSize} className="form-field">
              <FormController
                control={control}
                name={ProgramClosureFormKey.newOccupationalFunctionOther}
              >
                <MuiTextField
                  id={`${pageId}-${ProgramClosureFormKey.newOccupationalFunctionOther}`}
                  label={getLabel(
                    programSiteCoreKeys.newOccupationalFunctionOther,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  showCloseIcon
                  maxLength={MAX_LENGTH_75}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          )}
        </>
      )}

      <MuiGrid size={gridSize} className="form-field">
        <FormController
          control={control}
          name={ProgramClosureFormKey.newOccupationalLevel}
        >
          <MuiSelect
            id={`${pageId}-${ProgramClosureFormKey.newOccupationalLevel}`}
            label={getLabel(programSiteCoreKeys.occupationalLevelChangeStatus)}
            placeholder={commonLabels.selectPlaceHolder}
            options={fetchDropdownValues(
              programSiteCoreKeys.changeStatusModelPopupKey,
              programSiteCoreKeys.employerOccupationalLevelList,
            )}
            visibleDefaultSelectOption
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className="form-field">
        <FormController
          control={control}
          name={ProgramClosureFormKey.industryList}
        >
          <MuiSelect
            id={`${pageId}-${ProgramClosureFormKey.industryList}`}
            label={getLabel(programSiteCoreKeys.industryList)}
            placeholder={commonLabels.selectPlaceHolder}
            options={fetchDropdownValues(
              programSiteCoreKeys.changeStatusModelPopupKey,
              programSiteCoreKeys.industryList,
            )}
            visibleDefaultSelectOption
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className="form-field">
        <FormController
          control={control}
          name={ProgramClosureFormKey.industryChange}
        >
          <MuiSelect
            id={`${pageId}-${ProgramClosureFormKey.industryChange}`}
            label={getLabel(programSiteCoreKeys.industryChange)}
            placeholder={commonLabels.selectPlaceHolder}
            options={fetchDropdownValues(
              programSiteCoreKeys.changeStatusModelPopupKey,
              programSiteCoreKeys.industryChangeList,
            )}
            visibleDefaultSelectOption
            requiredARIALabel={commonARIALabel.required}
            isMandatory={industryChangeRule.mandatory}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className="status-field">
        <MuiDateFieldFormController
          control={control}
          name={ProgramClosureFormKey.newJobStartDate}
          className="date-picker"
          label={getLabel(programSiteCoreKeys.newJobStartDate)}
          id={`${pageId}-${ProgramClosureFormKey.newJobStartDate}`}
          disableManualInput
          format={userCountryDateFormat}
          value={
            getValues(ProgramClosureFormKey.newJobStartDate) !== ""
              ? dayjs(
                  getValues(ProgramClosureFormKey.newJobStartDate),
                  userCountryDateFormat,
                )
              : null
          }
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </MuiGrid>
      <MuiGrid size={gridSize} className="form-field">
        <FormController
          control={control}
          name={ProgramClosureFormKey.compensationComparison}
        >
          <MuiSelect
            id={`${pageId}-${ProgramClosureFormKey.compensationComparison}`}
            label={getLabel(programSiteCoreKeys.compensationComparison)}
            placeholder={commonLabels.selectPlaceHolder}
            options={fetchDropdownValues(
              programSiteCoreKeys.changeStatusModelPopupKey,
              programSiteCoreKeys.compensationComparisonList,
            )}
            visibleDefaultSelectOption
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      {currentSalaryRangeRule.visible && (
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.currentSalaryRange}
          >
            <MuiSelect
              id={`${pageId}-${ProgramClosureFormKey.currentSalaryRange}`}
              label={getLabel(programSiteCoreKeys.currentSalaryRange)}
              placeholder={commonLabels.selectPlaceHolder}
              options={countrySpecificOptions.currentSalaryRangeOptions}
              visibleDefaultSelectOption
              requiredARIALabel={commonARIALabel.required}
              isMandatory={currentSalaryRangeRule.mandatory}
            />
          </FormController>
        </MuiGrid>
      )}

      {newSalaryRangeRule.visible && (
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.newSalaryRange}
          >
            <MuiSelect
              id={`${pageId}-${ProgramClosureFormKey.newSalaryRange}`}
              label={getLabel(programSiteCoreKeys.newSalaryRange)}
              placeholder={commonLabels.selectPlaceHolder}
              options={countrySpecificOptions.newSalaryRangeOptions}
              visibleDefaultSelectOption
              requiredARIALabel={commonARIALabel.required}
              isMandatory={newSalaryRangeRule.mandatory}
            />
          </FormController>
        </MuiGrid>
      )}

      <MuiGrid size={gridSize} className="form-field">
        <FormController
          control={control}
          name={ProgramClosureFormKey.placementMethod}
        >
          <MuiSelect
            id={`${pageId}-${ProgramClosureFormKey.placementMethod}`}
            label={getLabel(programSiteCoreKeys.placementMethod)}
            placeholder={commonLabels.selectPlaceHolder}
            options={fetchDropdownValues(
              programSiteCoreKeys.changeStatusModelPopupKey,
              programSiteCoreKeys.placementMethodList,
            )}
            visibleDefaultSelectOption
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      {newEmployerVATNumberRule.visible && (
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.newEmployerVATNumber}
          >
            <MuiTextField
              id={`${pageId}-${ProgramClosureFormKey.newEmployerVATNumber}`}
              label={getLabel(programSiteCoreKeys.newEmployerVATNumber)}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_75}
              requiredARIALabel={commonARIALabel.required}
              isMandatory={newEmployerVATNumberRule.mandatory}
            />
          </FormController>
        </MuiGrid>
      )}

      {newLevelRule.mandatory && (
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.newLevel}
          >
            <MuiSelect
              id={`${pageId}-${ProgramClosureFormKey.newLevel}`}
              label={getLabel(programSiteCoreKeys.newLevel)}
              placeholder={commonLabels.selectPlaceHolder}
              options={countrySpecificOptions.newLevelOptions}
              visibleDefaultSelectOption
              requiredARIALabel={commonARIALabel.required}
              isMandatory={newLevelRule.mandatory}
            />
          </FormController>
        </MuiGrid>
      )}

      <MuiGrid size={gridSize} className="form-field">
        <FormController
          control={control}
          name={ProgramClosureFormKey.workLocationType}
        >
          <MuiSelect
            id={`${pageId}-${ProgramClosureFormKey.workLocationType}`}
            label={getLabel(programSiteCoreKeys.workLocationType)}
            placeholder={commonLabels.selectPlaceHolder}
            options={fetchDropdownValues(
              programSiteCoreKeys.changeStatusModelPopupKey,
              programSiteCoreKeys.workLocationTypeList,
            )}
            visibleDefaultSelectOption
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className="form-field">
        <MuiDateFieldFormController
          control={control}
          name={ProgramClosureFormKey.dateOfEndOfTrialPeriod}
          className="date-picker"
          label={getLabel(programSiteCoreKeys.dateOfEndOfTrialPeriod)}
          id={`${pageId}-${ProgramClosureFormKey.dateOfEndOfTrialPeriod}`}
          disableManualInput
          format={userCountryDateFormat}
          value={
            getValues(ProgramClosureFormKey.dateOfEndOfTrialPeriod) !== ""
              ? dayjs(
                  getValues(ProgramClosureFormKey.dateOfEndOfTrialPeriod),
                  userCountryDateFormat,
                )
              : null
          }
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
          isMandatory={
            dateOfEndOfTrialPeriodRule.mandatory && isNotEmptyNewJobStartDate
          }
        />
      </MuiGrid>

      <MuiGrid size={gridSize} className="form-field">
        <MuiDateFieldFormController
          control={control}
          name={ProgramClosureFormKey.solutionValidationDate}
          className="date-picker"
          label={getLabel(programSiteCoreKeys.solutionValidationDate)}
          id={`${pageId}-${ProgramClosureFormKey.solutionValidationDate}`}
          disableManualInput
          format={userCountryDateFormat}
          valueFormat={userCountryDateFormat}
          value={
            getValues(ProgramClosureFormKey.solutionValidationDate) !== ""
              ? dayjs(
                  getValues(ProgramClosureFormKey.solutionValidationDate),
                  userCountryDateFormat,
                )
              : null
          }
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
          isMandatory={solutionValidationDateRule.mandatory}
        />
      </MuiGrid>

      <MuiGrid size={gridSize} className="form-field">
        <FormController
          control={control}
          name={ProgramClosureFormKey.newEmploymentContractType}
        >
          <MuiSelect
            id={`${pageId}-${ProgramClosureFormKey.newEmploymentContractType}`}
            label={getLabel(programSiteCoreKeys.newEmploymentContractType)}
            placeholder={commonLabels.selectPlaceHolder}
            options={countrySpecificOptions.newEmploymentContractTypeOptions}
            visibleDefaultSelectOption
            requiredARIALabel={commonARIALabel.required}
            isMandatory={newEmploymentContractTypeRule.mandatory}
          />
        </FormController>
      </MuiGrid>
    </MuiGrid>
  );
};
